import { chakra } from '@chakra-ui/react';
import * as React from 'react';
import { HighlightProps } from 'react-instantsearch-core';
import { connectHighlight } from 'react-instantsearch-dom';

const getAttributeValue = (hit: any, attribute?: string): string => {
  if (!attribute) {
    return '';
  }

  const value = attribute
    .split('.')
    .reduce<any>((acc, key) => (acc != null ? acc[key] : undefined), hit);

  if (value == null) {
    return '';
  }

  return typeof value === 'string' ? value : String(value);
};

const getResultValue = (
  hit: any,
  property: '_highlightResult' | '_snippetResult',
  attribute?: string
): string | undefined => {
  if (!attribute) {
    return undefined;
  }

  const result = attribute
    .split('.')
    .reduce<any>((acc, key) => (acc != null ? acc[key] : undefined), hit?.[property]);

  if (result && typeof result === 'object' && 'value' in result) {
    return typeof result.value === 'string' ? result.value : undefined;
  }

  return undefined;
};

const parseHighlightedHtml = (
  value: string
): Array<{ value: string; isHighlighted: boolean }> => {
  const normalizedValue = value
    .replace(/<ais-highlight-[^>]*>/g, '<em>')
    .replace(/<\/ais-highlight-[^>]*>/g, '</em>');

  if (!normalizedValue.includes('<em>')) {
    return [{ value: normalizedValue, isHighlighted: false }];
  }

  return normalizedValue
    .split(/(<em>.*?<\/em>)/g)
    .filter(Boolean)
    .map((part) => ({
      value: part.replace(/<\/?em>/g, ''),
      isHighlighted: part.startsWith('<em>') && part.endsWith('</em>'),
    }));
};

const _CustomSnippet: React.FC<HighlightProps> = ({
  highlight,
  attribute,
  hit,
}) => {
  const safeAttribute = typeof attribute === 'string' ? attribute : '';
  if (!safeAttribute) {
    return null;
  }

  let parsedHit: Array<{ value: string; isHighlighted: boolean }> = [];

  try {
    parsedHit = highlight({
      highlightProperty: '_snippetResult',
      attribute: safeAttribute,
      hit,
    });
  } catch (_error) {
    parsedHit = [];
  }

  const aiSnippetValue =
    getResultValue(hit, '_snippetResult', safeAttribute) ||
    getResultValue(hit, '_highlightResult', safeAttribute);
  if (aiSnippetValue != null) {
    parsedHit = parseHighlightedHtml(aiSnippetValue);
  }

  if (!parsedHit.length) {
    return <span>{getAttributeValue(hit, safeAttribute)}</span>;
  }

  return (
    <span>
      {parsedHit.map((part, index) =>
        part.isHighlighted ? (
          <chakra.span key={index} bg="blue.100">
            {part.value}
          </chakra.span>
        ) : (
          <span key={index}>{part.value}</span>
        )
      )}
    </span>
  );
};

export const CustomSnippet = connectHighlight(_CustomSnippet);
